import { useEffect, useState } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import { formatEuros } from '@/lib/format'
import { cn } from '@/lib/utils'

export function Money({
  amount,
  secondaryAmount,
  align = 'end',
  className,
  emptySecondaryLabel,
}: {
  amount: number
  secondaryAmount?: number | null
  align?: 'start' | 'end'
  className?: string
  /** Tekst i.p.v. een tweede bedrag zolang secondaryAmount nog niet bekend is (bijv. "excl. btw" als er nog geen inkoopfactuur is). */
  emptySecondaryLabel?: string
}) {
  const showSecondary = secondaryAmount != null && Math.abs(secondaryAmount - amount) >= 0.005
  return (
    <span className={cn('pointer-events-auto inline-flex flex-col', align === 'start' ? 'items-start' : 'items-end')}>
      <CopyableAmount value={amount} className={cn('money leading-none', className)} />
      {showSecondary ? (
        <CopyableAmount value={secondaryAmount} className="money text-[13px] leading-none text-ink-faint" />
      ) : emptySecondaryLabel && secondaryAmount == null ? (
        <span className="text-[13px] leading-none text-ink-faint">{emptySecondaryLabel}</span>
      ) : null}
    </span>
  )
}

/** Klikken kopieert het bedrag naar het klembord; het label kleurt kort accent-700, geen toast. */
function CopyableAmount({ value, className }: { value: number; className?: string }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1500)
    return () => clearTimeout(timer)
  }, [copied])

  function copy(e: { stopPropagation: () => void }) {
    e.stopPropagation()
    navigator.clipboard?.writeText(formatEuros(value))
    setCopied(true)
  }

  return (
    <Tooltip content={copied ? null : 'Klik om te kopieren'} tapToToggle={false}>
      <span
        role="button"
        tabIndex={0}
        aria-label={`Kopieer ${formatEuros(value)}`}
        onClick={copy}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            copy(e)
          }
        }}
        className={cn(
          className,
          'cursor-pointer rounded-sm transition-colors duration-150 hover:text-accent-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
          copied && 'text-accent-700',
        )}
      >
        {formatEuros(value)}
      </span>
    </Tooltip>
  )
}
